// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';

import JobRequest, { JobRequestDTO } from '@shared/models/job-request';
import container from '@core/di';
import BaseStore from '@core/stores/base';
import JobRequestsService from '@shared/services/job-requests';
import { CollectionDTO } from '@shared/models/collection';
import { OrderDTO } from '@shared/models/order';
import { AssetDTO } from '@shared/models/asset';
import CommentsStore from './comments';
import { SDeliverableStatusChangeDTO } from '@shared/models/sp-sheets';

// NOTE: must use arrow function or 'this' may not be defined in certain contexts (eg. event handler).

@injectable()
export default class JobRequestsStore extends BaseStore<JobRequestDTO, JobRequest, JobRequestsService> {
  static diToken = Symbol('job-requests-store');
  commentsStore = new CommentsStore({ urlPrefix: 'job-request' });

  constructor() {
    super({ service: container.get<JobRequestsService>(JobRequestsService.diToken) });
  }

  // Call service method to check if order needs to be synced.
  syncOrderNeeded = async (orderId: number, lastRenderTime: Date): Promise<boolean> => {
    return await this.service.syncOrderNeeded(orderId, lastRenderTime);
  }

  createCollection = (
    orderId: OrderDTO['id'],
    assetId: AssetDTO['id'],
    jobRequestId: JobRequestDTO['id'],
    data: CollectionDTO
  ) => {
    return this.service.createCollection(orderId, assetId, jobRequestId, data);
  }

  getCollection = (collectionId: CollectionDTO['id']) => {
    return this.service.getCollection(collectionId);
  }

  editCollection = (
    collectionId: CollectionDTO['id'],
    data: CollectionDTO
  ) => {
    return this.service.editCollection(
      collectionId,
      data
    );
  }

  deleteCollection = (
    collectionId: CollectionDTO['id'],
    orderId: OrderDTO['id'],
    assetId: AssetDTO['id'],
    jobRequestId: JobRequestDTO['id']
  ) => {
    return this.service.deleteCollection(
      collectionId,
      orderId,
      assetId,
      jobRequestId
    );
  }

  // Update status of a single deliverable.  Used by Map and Sheet components (WXYZ REVERSE)
  updateDeliverableInfo = (
    orderId: OrderDTO['id'],
    assetId: AssetDTO['id'],
    jobRequestId: JobRequestDTO['id'],
    deliverable: Record<string, unknown>,
    blockId: string = ''
  ) => {
    return this.service.updateDeliverableInfo(orderId, assetId, jobRequestId, deliverable, blockId);
  }

  // Update status of a number of deliverables.
  updateDeliverableStatuses = (
    orderId: OrderDTO['id'],
    deliverableStatusChanges: Array<Partial<SDeliverableStatusChangeDTO>>
  ) => {
    return this.service.updateDeliverableStatuses(orderId, deliverableStatusChanges);
  }
  
  updateCollectionStatus = (
    orderId: OrderDTO['id'],
    jobRequestId: JobRequestDTO['id'],
    collection: { id: CollectionDTO['id']; status: CollectionDTO['status'] }
  ) => {
    return this.service.updateCollectionStatus(orderId, jobRequestId, collection);
  }
}
